import type { SanityTwoUpCard } from '#/lib/sanity/queries/sections';
import { createSanityFieldDataAttribute } from '#/lib/sanity/visualEditing';
import type { PageLayoutSharedProps } from '#/lib/types';
import { cn } from '#/lib/utils';

import { Button } from './Button';
import { FormattedText } from './FormattedText';
import { getHeadlineHighlightClass, Headline } from './Headline';
import { Media } from './Media';

export function TwoUpCard(props: SanityTwoUpCard & PageLayoutSharedProps) {
  const hasFormattedBodyCopy = Boolean(props.formattedBodyCopy?.length);

  return (
    <div className="p-main my-105 m:my-210">
      <div
        className="mx-auto flex max-w-1235 flex-col overflow-clip rounded-8 m:flex-row"
        style={{ background: props.contentBackground }}
      >
        <div className="pb-50 pl-30 pr-40 pt-30 m:w-2/5 m:pb-60 m:pl-65 m:pr-45 m:pt-50">
          <Headline
            value={props.headline}
            variant={5}
            highlightClass={getHeadlineHighlightClass({
              background: props.contentBackground,
            })}
            className="mb-25"
            dataSanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'headline',
            )}
          />
          {hasFormattedBodyCopy ? (
            <FormattedText
              value={props.formattedBodyCopy ?? []}
              className="max-w-[350px]"
              dataSanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'formattedBodyCopy',
              )}
            />
          ) : (
            <p
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'bodyCopy',
              )}
              className="max-w-[350px]"
            >
              {props.bodyCopy}
            </p>
          )}
          {props.cta ? (
            <Button
              className="mt-35"
              href={props.cta.href}
              target={props.cta.target}
              variant="secondary"
              data-sanity={createSanityFieldDataAttribute(
                props.sanitySource,
                'cta',
              )}
            >
              {props.cta.title}
            </Button>
          ) : null}
        </div>
        <div
          className={cn('m:w-3/5', {
            'p-30 m:p-75': props.hasMediaPadding,
            'm:-order-1': props.desktopLayout === 'imageText',
          })}
          style={{ background: props.mediaBackground ?? undefined }}
        >
          <div
            data-sanity={createSanityFieldDataAttribute(
              props.sanitySource,
              'media',
            )}
            className="size-full"
          >
            <Media
              media={props.media}
              className="size-full"
              mediaClassName={cn({
                'object-contain': props.hasMediaPadding,
                'object-cover': !props.hasMediaPadding,
              })}
              sizes={['100vw', { m: '60vw', '1285px': '740px' }]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
